import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Alert, Card, PageHeader, Spinner } from "@empite/ui-components";
import { useTenantBilling } from "../admin-hooks";
import { ErrorAlert, RequireOperator } from "./guards";
import { StatusBadge, when } from "./common";

/**
 * The two pages a hosted tier checkout sends the browser back to.
 *
 * THE REDIRECT IS NOT THE PAYMENT. Anyone can type this URL, and a real
 * provider may deliver the webhook seconds after the browser lands. Only the
 * verified webhook moves the tier, so the success page never announces an
 * activation it has not READ back from the overview: it polls until the tier it
 * was sent back for is the tier the company is on, and says "may take a moment"
 * until then.
 *
 * `tier` rides on the return URL, but it was put there by the Admin API off
 * ADMIN_PORTAL_URL and the plan it opened the checkout for — it only chooses
 * what this page waits for, never what the company holds.
 */

/** How often, and for how long, the success page re-reads the overview. */
const POLL_MS = 2000;
const POLL_FOR_MS = 60000;

export interface CheckoutReturnSearch {
  tier?: string;
}

export function CheckoutSuccess({ tier }: CheckoutReturnSearch) {
  return <RequireOperator>{() => <WaitForActivation tier={tier} />}</RequireOperator>;
}

function WaitForActivation({ tier }: CheckoutReturnSearch) {
  const [gaveUp, setGaveUp] = useState(false);
  const billing = useTenantBilling();
  const view = billing.data;
  const status = view?.subscription?.status;
  const activated =
    view !== undefined &&
    (tier === undefined || view.plan?.slug === tier) &&
    (status === "active" || status === "trialing");

  useEffect(() => {
    const timer = window.setTimeout(() => setGaveUp(true), POLL_FOR_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const { refetch } = billing;
  useEffect(() => {
    if (activated || gaveUp) {
      return;
    }
    const timer = window.setInterval(() => void refetch(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [activated, gaveUp, refetch]);

  return (
    <>
      <PageHeader
        title="Back from checkout"
        description="The payment provider has sent you back. Your tier changes when it confirms the charge to us, not when this page loads."
      />
      {billing.error ? (
        <ErrorAlert error={billing.error} title="Could not load your billing" />
      ) : null}
      {activated && view?.plan ? (
        <Alert tone="success" title={`${view.plan.name} is active`}>
          <p>
            The new allowance applies from the next request. The current period
            runs until{" "}
            <strong>{when(view.subscription?.current_period_end)}</strong>.
          </p>
        </Alert>
      ) : gaveUp ? (
        <Alert tone="warning" title="Activation is taking longer than usual">
          <p>
            We have not heard from the payment provider yet. Nothing is lost —
            the tier moves as soon as the confirmation arrives. Check{" "}
            <Link to="/billing">Billing</Link> again in a few minutes.
          </p>
        </Alert>
      ) : (
        <Alert tone="info" title="Activation may take a moment">
          <p>
            We are waiting for the payment provider to confirm the charge. This
            page checks every few seconds and updates by itself.
          </p>
          <Spinner label="Waiting for confirmation" />
        </Alert>
      )}
      <Card title="Your tier right now">
        {view?.plan ? (
          <p>
            {view.plan.name}{" "}
            {view.subscription ? <StatusBadge status={view.subscription.status} /> : null}
          </p>
        ) : (
          <p className="esm-muted-text">No tier yet.</p>
        )}
        <p>
          <Link to="/billing">Back to Billing</Link>
        </p>
      </Card>
    </>
  );
}

export function CheckoutCancelled() {
  return (
    <RequireOperator>
      {() => (
        <>
          <PageHeader title="Checkout cancelled" />
          <Alert tone="info" title="Nothing was charged">
            <p>
              You left the payment page before paying, so your tier has not
              changed. You can open a new checkout from Billing at any time.
            </p>
          </Alert>
          <Card>
            <p>
              <Link to="/billing">Back to Billing</Link>
            </p>
          </Card>
        </>
      )}
    </RequireOperator>
  );
}
